import { ConfigService } from '@/configuration/services/configService';
import { auth } from './index';
import { getSupportSession, saveSupportSession, takeSupportTicket, type ISupportSession } from './supportSession';

const endpoint = (action: string) =>
    `${ConfigService.get().application.apiUrl.replace(/\/$/, '')}/supportSessions/${action}`;
let opening: Promise<void> | null = null;

/** Single-flight also covers React StrictMode's repeated effects; the ticket is one-use. */
export function openSupportSession(): Promise<void> {
    if (opening) return opening;
    const ticket = takeSupportTicket();
    if (!ticket) return Promise.resolve();
    opening = (async () => {
        const response = await fetch(endpoint('open'), {
            method: 'POST',
            credentials: 'omit',
            cache: 'no-store',
            signal: AbortSignal.timeout(15000),
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ticket }),
        });
        if (!response.ok) {
            throw new Error(
                response.status === 401
                    ? 'Ссылка истекла или уже использована. Откройте кабинет заново из админки.'
                    : 'Не удалось войти от имени пользователя. Повторите вход из админки.',
            );
        }
        const data = (await response.json()) as ISupportSession;
        if (!data.accessToken || !data.userId || !data.userName || !Number.isFinite(Date.parse(data.expiresAt)))
            throw new Error('Сервер вернул неполные данные входа.');
        try {
            saveSupportSession(data);
        } catch {
            // Если вкладка не может сохранить вход, не оставляем созданную сессию открытой.
            await fetch(endpoint('close'), {
                method: 'POST',
                credentials: 'omit',
                cache: 'no-store',
                signal: AbortSignal.timeout(15000),
                headers: { Authorization: `Bearer ${data.accessToken}` },
            }).catch(() => undefined);
            throw new Error(
                'Браузер запретил хранение сессии в этой вкладке. Разрешите хранилище и повторите вход из админки.',
            );
        }
    })();
    return opening;
}

export async function closeSupportSession(): Promise<void> {
    const token = getSupportSession()?.accessToken;
    try {
        if (token) {
            const response = await fetch(endpoint('close'), {
                method: 'POST',
                credentials: 'omit',
                cache: 'no-store',
                signal: AbortSignal.timeout(15000),
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok && response.status !== 401) throw new Error('Не удалось завершить серверную сессию.');
        }
    } finally {
        auth.clearSession();
    }
}

const responseMessage = async (response: Response): Promise<string | undefined> => {
    try {
        const body = (await response.json()) as { message?: unknown };
        return typeof body?.message === 'string' ? body.message : undefined;
    } catch {
        return undefined;
    }
};

/** Продление запрещено (403: политика «с согласия пользователя», нет права, предел 4 ч) — повтор не поможет. */
export class SupportExtendForbiddenError extends Error {}

/** +30 минут к входу «от имени»: новый токен и срок сохраняются в хранилище вкладки. */
export async function extendSupportSession(): Promise<void> {
    const session = getSupportSession();
    if (!session) throw new Error('Вход от имени уже завершён.');
    const response = await fetch(endpoint('extend'), {
        method: 'POST',
        credentials: 'omit',
        cache: 'no-store',
        signal: AbortSignal.timeout(15000),
        headers: { Authorization: `Bearer ${session.accessToken}` },
    });
    if (response.status === 401) {
        // Сеанс закрыт или истёк на сервере: вкладка показывает «Вход от имени завершён».
        auth.clearSession();
        throw new Error('Вход от имени уже завершён.');
    }
    if (response.status === 403)
        throw new SupportExtendForbiddenError((await responseMessage(response)) ?? 'Продлить этот вход нельзя.');
    if (!response.ok)
        throw new Error((await responseMessage(response)) ?? 'Не удалось продлить вход. Попробуйте ещё раз.');
    const data = (await response.json()) as { accessToken?: string; expiresAt?: string };
    if (!data.accessToken || !data.expiresAt || !Number.isFinite(Date.parse(data.expiresAt)))
        throw new Error('Сервер вернул неполные данные продления.');
    const current = getSupportSession();
    if (!current) return;
    saveSupportSession({ ...current, accessToken: data.accessToken, expiresAt: data.expiresAt });
}
